import React, { useEffect, useState, useCallback } from 'react';
import { Chart, Axis, Geom, Tooltip, Coord } from 'bizcharts';
import { Card, Spin, Input, Select, Pagination, Checkbox, Radio, message } from 'antd';
import moment from 'moment';
import { useTaskContext } from '../../../../core/components/tasks/useTaskContext';
import { debounce } from 'lodash';
import { DEFAULT_STATUSES } from '../configs/task.config';
import { useHistory } from 'react-router-dom';

const { Search } = Input;
const { Option } = Select;

const TaskOverdueChart = () => {
  const { fetchTasksOverdue, currentUser, tasksOverdueLoading, overdueTasks } = useTaskContext();

  const chartRef = React.useRef(null);
  const today = moment();
  const history = useHistory();
  const [hoveredTaskId, setHoveredTaskId] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [userFilter, setUserFilter] = useState('all');

  const [visibleStatuses, setVisibleStatuses] = useState({
    '1–3 days late': true,
    '4–7 days late': true,
    '>7 days late': true,
  });

  const statusColors = {
    '1–3 days late': '#cae9ef',
    '4–7 days late': '#a6dae5',
    '>7 days late': '#95c4ce',
  };

  const handleFetchTasksOverdue = useCallback(
    debounce(async () => {
      try {
        await fetchTasksOverdue({
          variables: {
            where: {
              AND: [
                { status_in: DEFAULT_STATUSES },
                {
                  OR: [
                    {
                      createdBy: {
                        roles_some: {
                          org: {
                            OR: [
                              {
                                parentOrg_some: {
                                  id:
                                    currentUser &&
                                    currentUser.role &&
                                    currentUser.role.org &&
                                    currentUser.role.org.id,
                                },
                              },
                              {
                                id:
                                  currentUser &&
                                  currentUser.role &&
                                  currentUser.role.org &&
                                  currentUser.role.org.id,
                              },
                            ],
                          },
                        },
                      },
                    },
                  ],
                },
                {
                  dueDate_lt: new Date().toISOString(),
                },
                { dueDate_not: null },
              ],
            },
            orderBy: 'updatedAt_DESC',
            first: 1000,
            skip: 0,
          },
        });
      } catch (error) {
        message.error('Error fetching overdue tasks:', error);
      }
    }, 500),
    [fetchTasksOverdue, currentUser]
  );

  useEffect(() => {
    if (currentUser && currentUser.role && currentUser.role.org && currentUser.role.org.id) {
      handleFetchTasksOverdue();
    }
  }, [currentUser, handleFetchTasksOverdue]);

  const isUserInvolvedInTask = (task) => {
    if (!currentUser || !currentUser.id) return false;

    const isRequestor = task.createdBy && task.createdBy.id === currentUser.id;

    const isAssignee = task.assigneeUser && task.assigneeUser.id === currentUser.id;

    const isAssignees =
      task.assigneeUsers && task.assigneeUsers.some((assignee) => assignee.id === currentUser.id);

    const isFollower =
      task.followers && task.followers.some((follower) => follower.id === currentUser.id);

    const isCollaborator =
      task.collaborators &&
      task.collaborators.some((collaborator) => collaborator.id === currentUser.id);
    return isRequestor || isAssignee || isAssignees || isFollower || isCollaborator;
  };

  const transformedData = overdueTasks
    .map((task) => {
      if (!task.dueDate) return null;

      const dueDate = moment(task.dueDate);
      const diff = today.diff(dueDate, 'day');

      if (diff <= 0) return null;

      let status;
      if (diff <= 3) status = '1–3 days late';
      else if (diff <= 7) status = '4–7 days late';
      else status = '>7 days late';

      return {
        task: task.name || 'Task ' + task.id,
        overdue: diff,
        status: status,
        id: task.id,
        originalTask: task,
      };
    })
    .filter((item) => item !== null);

  const filteredData = transformedData.filter((item) => {
    const matchesSearch = item.task.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
    const isVisible = visibleStatuses[item.status];

    let matchesUserFilter = true;
    if (userFilter === 'myTasks') {
      matchesUserFilter = isUserInvolvedInTask(item.originalTask);
    }

    return matchesSearch && matchesStatus && isVisible && matchesUserFilter;
  });

  const startIndex = (currentPage - 1) * pageSize;
  const paginatedData = filteredData.slice(startIndex, startIndex + pageSize);

  const toggleLegendItem = (status) => {
    setVisibleStatuses((prev) => ({
      ...prev,
      [status]: !prev[status],
    }));
    setCurrentPage(1);
  };

  // Handle user filter change
  const handleUserFilterChange = (e) => {
    setUserFilter(e.target.value);
    setCurrentPage(1);
  };

  const CustomLegend = () => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '15px',
      }}
    >
      {Object.entries(statusColors)
        .filter(([status]) => status !== 'On Time')
        .map(([status, color]) => (
          <div
            key={status}
            style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              opacity: visibleStatuses[status] ? 1 : 0.5,
            }}
            onClick={() => toggleLegendItem(status)}
          >
            <Checkbox
              checked={visibleStatuses[status]}
              onChange={() => toggleLegendItem(status)}
              style={{ marginRight: '8px' }}
              onClick={(e) => e.stopPropagation()}
            />
            <div
              style={{
                width: '12px',
                height: '12px',
                backgroundColor: color,
                marginRight: '8px',
                borderRadius: '2px',
              }}
            />
            <span style={{ fontSize: '12px' }}>{status}</span>
          </div>
        ))}
    </div>
  );

  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.off('interval:click');
        chartRef.current.off('interval:mouseenter');
        chartRef.current.off('interval:mouseleave');
      }
    };
  }, []);

  if (tasksOverdueLoading) {
    return (
      <Card title="Tasks by Day(s) Overdue" style={{ width: '100%' }}>
        <Spin style={{ display: 'flex', justifyContent: 'center' }} />
      </Card>
    );
  }

  if (!transformedData.length) {
    return (
      <Card title="Tasks by Day(s) Overdue" style={{ width: '100%' }}>
        <div style={{ textAlign: 'center', padding: '50px', color: '#999' }}>
          No overdue tasks found
        </div>
      </Card>
    );
  }

  // Render detailed chart
  const renderChart = () => {
    return (
      <div
        onContextMenu={(e) => {
          if (hoveredTaskId) {
            e.preventDefault();
            window.open(`/tasks/details/${hoveredTaskId}`, '_blank');
          }
        }}
      >
        <Chart
        autoFit
        forceFit
        width="100%"
        height={Math.max(600, paginatedData.length * 25 + 150)}
        data={paginatedData}
        scale={{
          task: { type: 'cat' },
          overdue: { min: 0, nice: true },
        }}
        padding={[60, 80, 80, 150]}
        onGetG2Instance={(chart) => {
          chartRef.current = chart;
          chartRef.current.off('interval:click');
          chartRef.current.off('interval:mouseenter');
          chartRef.current.off('interval:mouseleave');

          chartRef.current.on('interval:mouseenter', (ev) => {
            const taskId = ev.data._origin.id;
            setHoveredTaskId(taskId);
          });

          chartRef.current.on('interval:mouseleave', () => {
            setHoveredTaskId(null);
          });

          // Left-click handler
          chartRef.current.on('interval:click', (ev) => {
            const taskId = ev.data._origin.id;
            history.push(`/tasks/details/${taskId}`);
          });
        }}
      >
        <Coord transpose />
        <Axis
          name="task"
          title={{ offset: 40, style: { fontSize: 12 } }}
          label={{
            style: { fontSize: 9, textAlign: 'center' },
            formatter: (text) => (text.length > 20 ? text.substring(0, 20) + '...' : text),
            rotate: 85,
          }}
        />
        <Axis
          name="overdue"
          title={{ offset: 40, style: { fontSize: 12 } }}
          label={{
            style: { fontSize: 10 },
            formatter: (val) => `${val} days late`,
          }}
        />
        <Tooltip shared showCrosshairs />
        <Geom
          type="interval"
          position="task*overdue"
          color={[
            'status',
            (status) => {
              return statusColors[status] || '#1890ff';
            },
          ]}
          size={Math.max(8, Math.min(25, 500 / paginatedData.length))}
        />
      </Chart>
      </div>
    );
  };

  return (
    <Card
      title={`Tasks by Day(s) Overdue (${filteredData.length} tasks)`}
      style={{ width: '100%' }}
      bodyStyle={{ width: '100%', padding: '16px' }}
    >
      <div
        style={{
          marginBottom: '16px',
          display: 'flex',
          gap: '10px',
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <Search
          placeholder="Search tasks..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          style={{ width: 250 }}
          allowClear
        />
        <Select
          value={statusFilter}
          onChange={(value) => {
            setStatusFilter(value);
            setCurrentPage(1);
          }}
          style={{ width: 150 }}
        >
          <Option value="all">All Status</Option>
          <Option value="1–3 days late">1–3 days late</Option>
          <Option value="4–7 days late">4–7 days late</Option>
          <Option value=">7 days late">{`>7 days late`}</Option>
        </Select>
        <Select
          value={pageSize}
          onChange={(value) => {
            setPageSize(value);
            setCurrentPage(1);
          }}
          style={{ width: 120 }}
        >
          <Option value={10}>10 per page</Option>
          <Option value={20}>20 per page</Option>
          <Option value={50}>50 per page</Option>
          <Option value={100}>100 per page</Option>
        </Select>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginLeft: 'auto',
          }}
        >
          <Radio.Group
            value={userFilter}
            onChange={handleUserFilterChange}
            buttonStyle="solid"
            size="medium"
          >
            <Radio.Button value="all">All Tasks</Radio.Button>
            <Radio.Button value="myTasks">My Tasks</Radio.Button>
          </Radio.Group>
        </div>
      </div>

      <CustomLegend />
      {renderChart()}

      {filteredData.length > pageSize && (
        <div style={{ marginTop: '16px', textAlign: 'center' }}>
          <Pagination
            current={currentPage}
            total={filteredData.length}
            pageSize={pageSize}
            onChange={setCurrentPage}
            showSizeChanger={false}
            showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} tasks`}
          />
        </div>
      )}
    </Card>
  );
};

export default TaskOverdueChart;
